import type { Skill } from '../domain.js';

/**
 * Природознавство: «Я досліджую світ» (3–4 клас) і пропедевтика
 * біології/фізики/хімії/географії (5–6 клас).
 *
 * Більшість навичок фактологічні — їх наповнює курований банк і LLM-пайплайн.
 * Процедурні генератори тут лише там, де є справжня варіативність
 * (перетворення одиниць, класифікації, розрахунки).
 */
export const SCIENCE_SKILLS: Skill[] = [
  // ── Жива природа ────────────────────────────────────────────────────────
  {
    id: 'science.life.classification',
    subject: 'science',
    strand: 'life',
    title: 'Класифікація живого',
    description: 'Розрізняє живе й неживе, групи тварин і рослин.',
    gradeBand: [3, 5],
    prerequisites: [],
    baseDifficulty: 880,
    foundational: true,
    generators: ['sci.classify'],
  },
  {
    id: 'science.life.plants',
    subject: 'science',
    strand: 'life',
    title: 'Рослини',
    description: 'Знає будову рослини, фотосинтез, умови росту.',
    gradeBand: [3, 6],
    prerequisites: ['science.life.classification'],
    baseDifficulty: 1000,
  },
  {
    id: 'science.life.animals',
    subject: 'science',
    strand: 'life',
    title: 'Тварини',
    description: 'Знає групи тварин, живлення, пристосування, ланцюги живлення.',
    gradeBand: [3, 6],
    prerequisites: ['science.life.classification'],
    baseDifficulty: 1020,
  },
  {
    id: 'science.life.human_body',
    subject: 'science',
    strand: 'life',
    title: 'Організм людини',
    description: 'Знає системи органів і їхні функції, правила здоров\'я.',
    gradeBand: [4, 6],
    prerequisites: ['science.life.classification'],
    baseDifficulty: 1110,
  },
  {
    id: 'science.life.ecology',
    subject: 'science',
    strand: 'life',
    title: 'Екологія',
    description: 'Розуміє зв\'язки в природі, вплив людини, сортування відходів.',
    gradeBand: [4, 6],
    prerequisites: ['science.life.animals'],
    baseDifficulty: 1090,
  },

  // ── Земля ───────────────────────────────────────────────────────────────
  {
    id: 'science.earth.weather_seasons',
    subject: 'science',
    strand: 'earth',
    title: 'Погода і пори року',
    description: 'Пояснює зміну пір року, читає погодні показники.',
    gradeBand: [3, 5],
    prerequisites: [],
    baseDifficulty: 900,
    generators: ['sci.weather'],
  },
  {
    id: 'science.earth.water_cycle',
    subject: 'science',
    strand: 'earth',
    title: 'Кругообіг води',
    description: 'Пояснює випаровування, конденсацію, опади.',
    gradeBand: [3, 6],
    prerequisites: ['science.physics.states_of_matter'],
    baseDifficulty: 1010,
  },
  {
    id: 'science.earth.rocks_soil',
    subject: 'science',
    strand: 'earth',
    title: 'Ґрунти й корисні копалини',
    description: 'Знає типи ґрунтів, корисні копалини України та їх використання.',
    gradeBand: [4, 6],
    prerequisites: [],
    baseDifficulty: 1090,
  },
  {
    id: 'science.space.solar_system',
    subject: 'science',
    strand: 'space',
    title: 'Сонячна система',
    description: 'Знає планети, їх порядок, обертання Землі й Місяця.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 990,
    generators: ['sci.solarSystem'],
  },

  // ── Фізика і хімія (пропедевтика) ───────────────────────────────────────
  {
    id: 'science.physics.states_of_matter',
    subject: 'science',
    strand: 'physics',
    title: 'Агрегатні стани',
    description: 'Розрізняє тверде, рідке, газоподібне; називає переходи між ними.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 950,
    foundational: true,
    generators: ['sci.statesOfMatter'],
  },
  {
    id: 'science.physics.forces_motion',
    subject: 'science',
    strand: 'physics',
    title: 'Сили й рух',
    description: 'Розуміє тяжіння, тертя, опір, наслідки дії сили.',
    gradeBand: [5, 6],
    prerequisites: ['science.physics.states_of_matter'],
    baseDifficulty: 1170,
  },
  {
    id: 'science.physics.light_sound',
    subject: 'science',
    strand: 'physics',
    title: 'Світло і звук',
    description: 'Пояснює поширення світла й звуку, тінь, відлуння, веселку.',
    gradeBand: [4, 6],
    prerequisites: [],
    baseDifficulty: 1100,
  },
  {
    id: 'science.physics.electricity',
    subject: 'science',
    strand: 'physics',
    title: 'Електрика і магнетизм',
    description: 'Знає провідники й ізолятори, просте коло, магніти, безпеку.',
    gradeBand: [5, 6],
    prerequisites: ['science.physics.forces_motion'],
    baseDifficulty: 1200,
  },
  {
    id: 'science.chem.substances',
    subject: 'science',
    strand: 'chem',
    title: 'Речовини та їх властивості',
    description: 'Розрізняє суміші й чисті речовини, знає способи розділення.',
    gradeBand: [5, 6],
    prerequisites: ['science.physics.states_of_matter'],
    baseDifficulty: 1190,
  },

  // ── Географія ───────────────────────────────────────────────────────────
  {
    id: 'science.geo.ukraine',
    subject: 'science',
    strand: 'geo',
    title: 'Географія України',
    description: 'Знає області, річки, гори, моря, сусідні держави.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 1030,
    generators: ['sci.geoUkraine'],
  },
  {
    id: 'science.geo.world',
    subject: 'science',
    strand: 'geo',
    title: 'Материки й океани',
    description: 'Знає материки, океани, столиці та природні зони.',
    gradeBand: [4, 6],
    prerequisites: [],
    baseDifficulty: 1080,
    generators: ['sci.geoWorld'],
  },
  {
    id: 'science.geo.map_skills',
    subject: 'science',
    strand: 'geo',
    title: 'Робота з картою',
    description: 'Орієнтується за сторонами світу, читає масштаб і умовні знаки.',
    gradeBand: [4, 6],
    prerequisites: ['science.geo.ukraine'],
    baseDifficulty: 1130,
    generators: ['sci.mapSkills'],
  },
];
